import { CSSProperties, ReactNode, Suspense } from "react";
import styled, { css } from "styled-components";
import { ArtRoot } from "features/onboarding/art/kit";
import { softPanelBackground } from "features/onboarding/common/onboardingUi";

type StageProps = {
  children: ReactNode;
  /** Plain: no tinted panel behind the art. */
  plain?: boolean;
  /** Fixed stage heights; the drawing scales to fit and stays centered. */
  height?: number;
  phoneHeight?: number;
  className?: string;
  style?: CSSProperties;
};

/**
 * The inset well that holds an illustration. The art's labels may load other
 * translation namespaces, so it has its own Suspense boundary: the panel keeps
 * its place while they load.
 */
const ArtStage = ({
  children,
  plain,
  height,
  phoneHeight,
  className,
  style,
}: StageProps) => (
  <Stage
    $plain={!!plain}
    $height={height}
    $phoneHeight={phoneHeight ?? height}
    className={className}
    style={style}
  >
    <Suspense fallback={null}>
      <ArtRoot>{children}</ArtRoot>
    </Suspense>
  </Stage>
);

export default ArtStage;

const Stage = styled.div<{
  $plain: boolean;
  $height?: number;
  $phoneHeight?: number;
}>`
  position: relative;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  border-radius: ${(p) => p.theme.radii.control}px;
  ${(p) =>
    !p.$plain &&
    css`
      /* An inset well: flat, no gradient. */
      background: ${softPanelBackground(p.theme)};
      border: 1px solid ${p.theme.colors.cardBorderColor};
      padding: 8px;
    `}
  ${(p) =>
    p.$height &&
    css`
      height: ${p.$height}px;
      > ${ArtRoot}, > ${ArtRoot} div {
        height: 100%;
      }
      svg.ak-canvas {
        height: 100%;
      }
      @media (max-width: 560px) {
        height: ${p.$phoneHeight}px;
      }
    `}
  /* Too small on phones for words: the drawing reads as a picture. */
  ${(p) =>
    (p.$phoneHeight ?? p.$height ?? Infinity) < 100 &&
    css`
      @media (max-width: 560px) {
        svg.ak-canvas text {
          display: none;
        }
      }
    `}
`;
